import { OverlayPanel } from 'primereact/overlaypanel';
import PropTypes from 'prop-types';
import React, { cloneElement, forwardRef, useCallback, useRef } from 'react';

/**
 * A wrapper around PrimeReact's OverlayPanel that provides popover functionality.
 *
 * @param {function} onShow - Callback when popover is shown
 * @param {function} onHide - Callback when popover is hidden
 * @param {boolean} showCloseIcon - Show close icon in popover
 */
const WrappedPopover = forwardRef(
	(
		{ children, triggerElement, onShow, onHide, className = '', showCloseIcon },
		ref
	) => {
		// If no external ref is provided, create an internal one
		const internalRef = useRef(null);
		const overlayRef = ref || internalRef;

		// Handles showing the popover using OverlayPanel's show method
		// and calls the optional onShow callback.
		const handleShow = useCallback(
			event => {
				if (overlayRef.current) {
					overlayRef.current.show(event);
					if (onShow) onShow();
				}
			},
			[onShow]
		);

		// Calls the onHide callback when the popover is closed,
		// allowing the parent component to react (e.g., update state, cleanup).
		const handleHide = useCallback(() => {
			if (onHide) onHide();
		}, [onHide]);

		// Prevents click events within the popover from propagating to parent elements.
		const handleOverlayClick = useCallback(event => {
			event.stopPropagation();
		}, []);

		// Manages trigger element (click) interaction to open the popover,
		// prevents event propagation, and calls handleShow.
		const handleTriggerInteraction = useCallback(
			event => {
				event.stopPropagation();

				handleShow(event);
			},
			[handleShow, triggerElement]
		);

		// Defines interaction props for the trigger element.
		const triggerProps = {
			onClick: handleTriggerInteraction,
		};

		return (
			<div className={`common-ui-popover ${className}`}>
				{triggerElement ? (
					cloneElement(triggerElement, triggerProps)
				) : (
					<div
						className="trigger"
						onClick={handleTriggerInteraction}
						role="presentation"
					></div>
				)}
				<div onClick={handleOverlayClick} role="presentation">
					<OverlayPanel
						ref={overlayRef}
						showCloseIcon={showCloseIcon}
						onHide={handleHide}
						data-qa="ui-popover"
						className={`common-ui-popover ${className}`}
					>
						{children}
					</OverlayPanel>
				</div>
			</div>
		);
	}
);

WrappedPopover.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node.isRequired,
	showCloseIcon: PropTypes.bool,
	onShow: PropTypes.func,
	onHide: PropTypes.func,
	triggerElement: PropTypes.element,
	ref: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
	]),
};

WrappedPopover.defaultProps = {
	className: '',
	showCloseIcon: true,
	onShow: undefined,
	onHide: undefined,
};

WrappedPopover.displayName = 'WrappedPopover';

export default WrappedPopover;
